/**
 * HelpCenter — collapsible Q&A surface for the Safety page.
 * Tabs: Platform · Fryqnc · Pulsynq · MindLynq.
 * Each tab renders its sections as accordions.
 *
 * Searchable: live-filter Qs by keyword across whichever tab is active.
 */
import { useMemo, useState } from "react";
import { Search, MessageCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { APPS } from "@/lib/apps";
import {
  PLATFORM_HELP,
  APP_HELP,
  type HelpSection,
} from "@/lib/safety/helpContent";

const filterSections = (sections: HelpSection[], q: string): HelpSection[] => {
  if (!q.trim()) return sections;
  const needle = q.trim().toLowerCase();
  return sections
    .map((s) => ({
      ...s,
      items: s.items.filter(
        (i) =>
          i.q.toLowerCase().includes(needle) ||
          i.a.toLowerCase().includes(needle),
      ),
    }))
    .filter((s) => s.items.length > 0);
};

const SectionList = ({ sections }: { sections: HelpSection[] }) => {
  if (sections.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-card/40 p-8 text-center">
        <p className="text-sm font-medium">No matches in this section.</p>
        <p className="mt-2 text-xs text-muted-foreground">
          Try a different keyword, switch tabs, or ask RAELA below.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-8">
      {sections.map((s) => (
        <div key={s.id}>
          <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            {s.title}
          </h3>
          {s.description && (
            <p className="mt-1 text-xs text-muted-foreground">{s.description}</p>
          )}
          <Accordion type="single" collapsible className="mt-3 rounded-2xl border border-border bg-card overflow-hidden">
            {s.items.map((item, idx) => (
              <AccordionItem key={`${s.id}-${idx}`} value={`${s.id}-${idx}`} className="border-border">
                <AccordionTrigger className="px-5 text-left hover:no-underline hover:bg-secondary/50">
                  <span className="text-sm font-medium pr-3">{item.q}</span>
                </AccordionTrigger>
                <AccordionContent className="px-5 pb-4 text-sm text-muted-foreground leading-relaxed">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      ))}
    </div>
  );
};

const HelpCenter = () => {
  const [query, setQuery] = useState("");

  const platform = useMemo(() => filterSections(PLATFORM_HELP, query), [query]);
  const fryqnc = useMemo(() => filterSections(APP_HELP.fryqnc, query), [query]);
  const pulsynq = useMemo(() => filterSections(APP_HELP.pulsynq, query), [query]);
  const mindlynq = useMemo(() => filterSections(APP_HELP.mindlynq, query), [query]);

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Ask RAELA</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Answers for every member, across every Wavlynq community.
          </p>
        </div>
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" aria-hidden />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help…"
            className="pl-9"
          />
        </div>
      </div>

      <Tabs defaultValue="platform" className="mt-6">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="platform">Platform</TabsTrigger>
          {APPS.map((a) => (
            <TabsTrigger key={a.id} value={a.id}>
              {a.name}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="platform" className="mt-6">
          <SectionList sections={platform} />
        </TabsContent>
        <TabsContent value="fryqnc" className="mt-6">
          <SectionList sections={fryqnc} />
        </TabsContent>
        <TabsContent value="pulsynq" className="mt-6">
          <SectionList sections={pulsynq} />
        </TabsContent>
        <TabsContent value="mindlynq" className="mt-6">
          <SectionList sections={mindlynq} />
        </TabsContent>
      </Tabs>

      {/* Ask RAELA fallback — required by spec */}
      <div className="mt-10 rounded-2xl border border-border bg-secondary/40 p-5 flex items-start gap-4">
        <MessageCircle className="h-5 w-5 text-foreground shrink-0 mt-0.5" aria-hidden />
        <div className="flex-1">
          <p className="text-sm font-semibold">Didn't find what you need?</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Ask RAELA. She's trained on every help article and stays with you.
          </p>
        </div>
        <Link
          to="/raela"
          className="rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium hover:opacity-90 transition-opacity whitespace-nowrap"
        >
          Ask RAELA
        </Link>
      </div>
    </div>
  );
};

export default HelpCenter;
